import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  ChevronDown,
  Sparkles,
  Gamepad2,
  Send,
  Instagram,
  Youtube,
  Facebook,
  Shield,
  Palette,
  UserPlus
} from "lucide-react";
import { LucideIcon } from "lucide-react";

interface HeaderProps {
  data: any;
  filteredNavItems: any[];
  setIsSupporterModalOpen: (open: boolean) => void;
  setIsRegistrationModalOpen: (open: boolean) => void;
}

export const Header: React.FC<HeaderProps> = ({
  data,
  filteredNavItems,
  setIsSupporterModalOpen,
  setIsRegistrationModalOpen,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isSocialDropdownOpen, setIsSocialDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const {
    discordUrl,
    telegramUrl,
    facebookUrl,
    instagramUrl,
    youtubeUrl,
    sponsorUrl,
    meuSetupUrl,
  } = (data && data.siteConfig) ? data.siteConfig : {} as any;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const isParentActive = (subItems?: { path: string }[]) => {
    if (!subItems) return false;
    return subItems.some((sub) => location.pathname.startsWith(sub.path));
  };

  const handleMobileLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Mobile Header */}
      <div className={`md:hidden flex items-center justify-between p-4 sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#020617]/95 backdrop-blur-xl border-b border-white/10 shadow-lg' : 'bg-transparent border-b border-transparent'}`}>
        <Link
          to="/"
          className="flex items-center gap-3 transition-opacity active:opacity-70"
          onClick={handleMobileLinkClick}
        >
          <div className="w-10 h-10 rounded-xl overflow-hidden border border-white/10 shadow-lg shadow-black/50">
            <img
              src="/logo_main.png"
              alt="Games no Linux"
              className="w-full h-full object-cover"
            />
          </div>
          <span className="font-heading font-bold text-xl text-white tracking-tight">
            Games no Linux
          </span>
        </Link>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white p-2 hover:bg-white/10 rounded-xl transition-colors focus:outline-none"
          aria-label={isMobileMenuOpen ? "Fechar Menu" : "Abrir Menu"}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Desktop Navigation */}
      <header
        className={`
        hidden md:block fixed top-0 w-full z-50 transition-all duration-500
        ${
          isScrolled
            ? "bg-[#020617]/95 backdrop-blur-xl border-b border-white/10 shadow-2xl py-2"
            : location.pathname === "/"
              ? "bg-transparent border-b border-transparent py-4"
              : "bg-[#020617] border-b border-white/10 py-3"
        }
      `}
      >
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-4 group">
            <div
              className={`relative rounded-xl border border-white/10 overflow-hidden group-hover:shadow-[0_0_20px_rgba(20,184,166,0.3)] transition-all duration-500 shadow-2xl shadow-black/50 ${isScrolled ? "w-10 h-10" : "w-14 h-14"}`}
            >
              <img
                src="/logo_main.png"
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-col">
              <span
                className={`font-heading font-bold text-white tracking-tight leading-none group-hover:text-[#14B8A6] transition-all duration-300 ${isScrolled ? "text-lg" : "text-2xl"}`}
              >
                Games no Linux
              </span>
              <span className={`text-[#14B8A6] font-semibold tracking-[0.25em] uppercase transition-all duration-300 ${isScrolled ? 'text-[8px] mt-0.5 opacity-70' : 'text-[10px] mt-1.5 opacity-90'}`}>
                Universo Hytale
              </span>
            </div>
          </Link>

          <nav className="flex items-center gap-1">
            {filteredNavItems.map((item) => {
              const isActive =
                item.path === location.pathname ||
                isParentActive(item.subItems);

              return (
                <div
                  key={item.label}
                  className="relative group"
                  onMouseEnter={() =>
                    item.subItems && setActiveDropdown(item.label)
                  }
                  onMouseLeave={() => item.subItems && setActiveDropdown(null)}
                >
                  {item.subItems ? (
                    item.path ? (
                      <NavLink
                        to={item.path}
                        className={`
                          flex items-center gap-1.5 px-4 py-2 text-sm font-medium tracking-wide transition-colors duration-200 cursor-pointer focus:outline-none
                          ${isActive || activeDropdown === item.label ? "text-primary-500" : "text-slate-400 hover:text-white"}
                        `}
                        aria-haspopup="true"
                        aria-expanded={activeDropdown === item.label}
                      >
                        {item.label}{" "}
                        <ChevronDown
                          size={14}
                          className={`transition-transform duration-300 ${activeDropdown === item.label ? "rotate-180" : "group-hover:rotate-180"}`}
                        />
                      </NavLink>
                    ) : (
                      <button
                        className={`
                          flex items-center gap-1.5 px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors duration-200 cursor-pointer focus:outline-none
                          ${isActive || activeDropdown === item.label ? "text-primary-500" : "text-slate-400 hover:text-white"}
                        `}
                        aria-haspopup="true"
                        aria-expanded={activeDropdown === item.label}
                      >
                        {item.label}{" "}
                        <ChevronDown
                          size={14}
                          className={`transition-transform duration-300 ${activeDropdown === item.label ? "rotate-180" : "group-hover:rotate-180"}`}
                        />
                      </button>
                    )
                  ) : (
                    <NavLink
                      to={item.path || "#"}
                      className={({ isActive }) => `
                         block px-4 py-2 text-sm font-medium tracking-wide transition-colors duration-200
                         ${isActive ? "text-[#14B8A6]" : "text-slate-400 hover:text-white"}
                      `}
                    >
                      {item.label}
                    </NavLink>
                  )}

                  {/* Dropdown Menu */}
                  {item.subItems && (
                    <div
                      className={`
                      absolute left-1/2 -translate-x-1/2 top-full pt-4 w-72
                      transition-all duration-300 origin-top z-50
                      ${activeDropdown === item.label ? "opacity-100 scale-100 visible" : "opacity-0 scale-95 invisible"}
                    `}
                    >
                      <div className="bg-[#020617]/90 backdrop-blur-xl border border-white/10 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.6)] overflow-hidden p-2">
                        {item.subItems.map((sub: any) => {
                          const isSubActive = location.pathname === sub.path;
                          return (
                            <NavLink
                              key={sub.path}
                              to={sub.path}
                              className={`
                                flex items-start gap-4 px-4 py-3 rounded-xl transition-all duration-200
                                ${isSubActive ? "bg-[#14B8A6]/10" : "hover:bg-white/5"}
                              `}
                            >
                              <div
                                className={`mt-1 p-2 rounded-lg ${isSubActive ? "bg-[#14B8A6]/20 text-[#14B8A6]" : "bg-white/5 text-slate-400"}`}
                              >
                                <sub.icon size={18} />
                              </div>
                              <div>
                                <div
                                  className={`text-sm font-bold tracking-tight ${isSubActive ? "text-[#14B8A6]" : "text-white"}`}
                                >
                                  {sub.label}
                                </div>
                                {sub.description && (
                                  <div className="text-[11px] text-slate-400 font-medium mt-0.5 leading-tight">
                                    {sub.description}
                                  </div>
                                )}
                              </div>
                            </NavLink>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Action Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <button
              onClick={() => setIsSupporterModalOpen(true)}
               className="flex items-center gap-2 px-4 py-2 text-sm font-medium tracking-wide text-[#F59E0B] hover:text-[#FCD34D] transition-colors group whitespace-nowrap"
            >
              <Sparkles
                size={16}
                className="group-hover:rotate-12 transition-transform shrink-0"
              />
              Apoiador
            </button>

            <div className="h-4 w-px bg-white/10 mx-1"></div>
            <button
              onClick={() => setIsRegistrationModalOpen(true)}
              className="btn btn-primary !py-2.5 !text-sm !px-6 whitespace-nowrap"
              aria-label="Registrar para entrar no servidor"
            >
              Entrar no Servidor
            </button>

            <div className="h-4 w-px bg-white/10 mx-1"></div>

            <div
              className="relative"
              onMouseEnter={() => setIsSocialDropdownOpen(true)}
              onMouseLeave={() => setIsSocialDropdownOpen(false)}
            >
              <button
                 className={`flex items-center gap-2 px-4 py-2 text-sm font-medium tracking-wide text-slate-400 hover:text-white transition-colors focus:outline-none ${isSocialDropdownOpen ? "text-[#14B8A6]" : ""}`}
                aria-haspopup="true"
                aria-expanded={isSocialDropdownOpen}
              >
                Redes Sociais
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-300 ${isSocialDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>

              {/* Social Dropdown Menu */}
              <div
                className={`
                absolute right-0 top-full pt-4 w-56
                transition-all duration-300 origin-top z-50
                ${isSocialDropdownOpen ? "opacity-100 scale-100 visible" : "opacity-0 scale-95 invisible"}
              `}
              >
                <div className="bg-[#020617]/90 backdrop-blur-xl border border-white/10 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.6)] overflow-hidden p-2">
                  <DropdownSocialLink
                    href={discordUrl}
                    icon={Gamepad2}
                    label="Discord"
                    color="hover:bg-[#5865F2]/10 hover:text-[#5865F2]"
                  />
                  <DropdownSocialLink
                    href={telegramUrl}
                    icon={Send}
                    label="Telegram"
                    color="hover:bg-[#26A5E4]/10 hover:text-[#26A5E4]"
                  />
                  <DropdownSocialLink
                    href={instagramUrl}
                    icon={Instagram}
                    label="Instagram"
                    color="hover:bg-[#E4405F]/10 hover:text-[#E4405F]"
                  />
                  <DropdownSocialLink
                    href={youtubeUrl}
                    icon={Youtube}
                    label="YouTube"
                    color="hover:bg-[#FF0000]/10 hover:text-[#FF0000]"
                  />
                  <DropdownSocialLink
                    href={facebookUrl}
                    icon={Facebook}
                    label="Facebook"
                    color="hover:bg-[#1877F2]/10 hover:text-[#1877F2]"
                  />
                  <div className="h-px bg-white/5 my-1 mx-2"></div>
                  <Link
                    to="/sponsors"
                    className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 hover:bg-white/5 group/link hover:bg-amber-500/10 hover:text-amber-500"
                  >
                    <div className="p-2 rounded-lg bg-white/5 transition-colors group-hover/link:bg-current/10">
                      <Shield size={18} />
                    </div>
                    <span className="text-sm font-bold tracking-tight text-white group-hover/link:text-current">
                      Patrocinadores Oficiais
                    </span>
                  </Link>
                  <DropdownSocialLink
                    href={meuSetupUrl || "#"}
                    icon={Palette}
                    label="Meu Setup"
                    color="hover:bg-blue-500/10 hover:text-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-surface-950/98 backdrop-blur-xl pt-24 px-6 pb-12 md:hidden overflow-y-auto animate-fade-in">
          <nav className="flex flex-col space-y-4 max-w-sm mx-auto">
            {filteredNavItems.map((item) => (
              <div
                key={item.label}
                className="border-b border-white/5 last:border-0 pb-4 last:pb-0"
              >
                {item.subItems ? (
                  <div className="space-y-3">
                    <div className="text-[#14B8A6] font-bold uppercase tracking-wider text-xs px-2 opacity-80">
                      {item.label}
                    </div>
                    <div className="grid grid-cols-1 gap-1">
                      {item.subItems.map((sub: any) => (
                        <NavLink
                          key={sub.path}
                          to={sub.path}
                          onClick={handleMobileLinkClick}
                          className={({ isActive }) => `
                              flex items-center gap-3 p-3 rounded-lg transition-colors
                              ${isActive ? "bg-[#14B8A6]/10 text-[#14B8A6] font-bold" : "text-slate-300 hover:bg-white/5"}
                            `}
                        >
                          <sub.icon size={18} />
                          {sub.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                ) : (
                  <NavLink
                    to={item.path || "#"}
                    onClick={handleMobileLinkClick}
                    className={({ isActive }) => `
                      flex items-center gap-4 text-lg font-bold p-3 rounded-lg transition-colors
                      ${isActive ? "bg-white/10 text-[#14B8A6]" : "text-slate-200 hover:bg-white/5"}
                    `}
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon
                          size={24}
                          className={
                            isActive ? "text-[#14B8A6]" : "text-slate-500"
                          }
                        />
                        {item.label}
                      </>
                    )}
                  </NavLink>
                )}
              </div>
            ))}

            <div className="flex flex-col gap-3 pt-4">
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsSupporterModalOpen(true);
                }}
                className="btn btn-secondary w-full !py-4 !bg-[#F59E0B]/10 !border-[#F59E0B]/20 !text-[#F59E0B]"
              >
                <Sparkles size={20} className="shrink-0" />
                Seja Apoiador
              </button>

              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsRegistrationModalOpen(true);
                }}
                className="btn btn-primary w-full !py-4"
                aria-label="Abrir formulário de registro para o servidor"
              >
                <UserPlus size={20} className="shrink-0" />
                Entrar no Servidor
              </button>
              <div className="grid grid-cols-5 gap-2">
                <MobileSocialLink
                  href={discordUrl}
                  icon={Gamepad2}
                  color="!bg-[#5865F2] !border-[#5865F2]"
                />
                <MobileSocialLink
                  href={telegramUrl}
                  icon={Send}
                  color="!bg-[#26A5E4] !border-[#26A5E4]"
                />
                <MobileSocialLink
                  href={instagramUrl}
                  icon={Instagram}
                  color="!bg-[#E4405F] !border-[#E4405F]"
                />
                <MobileSocialLink
                  href={youtubeUrl}
                  icon={Youtube}
                  color="!bg-[#FF0000] !border-[#FF0000]"
                />
                <MobileSocialLink
                  href={facebookUrl}
                  icon={Facebook}
                  color="!bg-[#1877F2] !border-[#1877F2]"
                />
              </div>
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

const DropdownSocialLink: React.FC<{
  href: string;
  icon: LucideIcon;
  label: string;
  color: string;
}> = ({ href, icon: Icon, label, color }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    aria-label={`Visitar nosso ${label}`}
    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 hover:bg-white/5 group/link ${color}`}
  >
    <div className={`p-2 rounded-lg bg-white/5 transition-colors group-hover/link:bg-current/10`}>
      <Icon size={18} />
    </div>
    <span className="text-sm font-bold tracking-tight text-white group-hover/link:text-current">
      {label}
    </span>
  </a>
);

const MobileSocialLink: React.FC<{
  href: string;
  icon: LucideIcon;
  color: string;
}> = ({ href, icon: Icon, color }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    aria-label="Link de rede social mobile"
    className={`btn btn-secondary flex items-center justify-center !py-4 !rounded-xl ${color}`}
  >
    <Icon size={20} className="shrink-0" />
  </a>
);
